import {
  Blockchain,
  type BlockchainConfig,
} from "@anaconda/core/blockchain/types"
import { atom, computed } from "@reatom/core"
import { molecule } from "bunshi"
import type {
  Account,
  Chain,
  PublicClient,
  Transport,
  WalletClient,
} from "viem"
import { BlockchainsMoleculeReatom } from "../blockchains/blockchain-molecule.reatom"
import {
  resolvePrimaryUnionAddress,
  toEvmUnionAddress,
  toSolanaUnionAddress,
} from "./connected-addresses"

export type WaletConnectStatus =
  | "connected"
  | "disconnected"
  | "connecting"
  | undefined

export type LoginMethod = "wallet" | "email" | "global_wallet" | undefined

/**
 * - `prompted`: the wallet prompt opened for a locked or disconnected wallet.
 * - `restoring`: the wallet is still being restored and connects on its own.
 * - `unavailable`: no wallet prompt applies to this Profile.
 */
export type EvmReconnectResult = "prompted" | "restoring" | "unavailable"

export const WalletConnectMoleculeReatom = molecule((mol) => {
  const { blockchains } = mol(BlockchainsMoleculeReatom)

  const walletBlockchainNetworkId = atom<number | undefined>(
    undefined,
    "walletBlockchainNetworkId",
  )

  const status = atom<WaletConnectStatus>(undefined, "walletConnectStatus")

  const evmAddress = atom<string | undefined>(undefined, "evmWalletAddress")

  const solanaAddress = atom<string | undefined>(
    undefined,
    "solanaWalletAddress",
  )

  const initialized = atom<boolean>(false, "walletInitialized")

  const loginMethod = atom<LoginMethod>(undefined, "loginMethod")

  const hasEvmWallet = computed(() => !!evmAddress(), "hasEvmWallet")

  const hasSolanaWallet = computed(() => !!solanaAddress(), "hasSolanaWallet")

  const evmUnionAddress = computed(
    () => toEvmUnionAddress(evmAddress()),
    "evmUnionAddress",
  )

  const solanaUnionAddress = computed(
    () => toSolanaUnionAddress(solanaAddress()),
    "solanaUnionAddress",
  )

  /** @deprecated Use evmUnionAddress / solanaUnionAddress. EVM-first aggregate for legacy single-wallet callers. */
  const primaryUnionAddress = computed(
    () => resolvePrimaryUnionAddress(evmUnionAddress(), solanaUnionAddress()),
    "primaryUnionAddress",
  )

  /** @deprecated Use primaryUnionAddress or evmUnionAddress / solanaUnionAddress directly. */
  const unionAddress = computed(() => primaryUnionAddress(), "unionAddress")

  /** @deprecated Use evmAddress / solanaAddress. */
  const address = computed(
    () => evmAddress() ?? solanaAddress(),
    "walletAddress",
  )

  /** @deprecated Use hasEvmWallet / hasSolanaWallet. */
  const chain = computed(() => {
    if (evmAddress()) {
      return Blockchain.ETHEREUM
    }

    if (solanaAddress()) {
      return Blockchain.SOLANA
    }

    return undefined
  }, "connectedChain")

  const isConnected = computed(
    () => hasEvmWallet() || hasSolanaWallet(),
    "isConnected",
  )

  const isConnecting = computed(() => status() === "connecting", "isConnecting")

  const connectedBlockchain = computed(() => {
    const blockchainsQuery = blockchains()
    const connectedNet = walletBlockchainNetworkId()
    const chainGroup = chain()

    if (!blockchainsQuery.data) return blockchainsQuery

    return {
      ...blockchainsQuery,
      data: getBlockchain(
        blockchainsQuery.data.networks,
        chainGroup,
        connectedNet,
      ),
    }
  }, "connectedBlockchain")

  const balanceQueryParams = computed(
    () => ({
      queryKey: ["balance", walletBlockchainNetworkId(), evmAddress()],
      queryFn: async () => {
        const { getBalance } = getBalanceAtom()
        return getBalance()
      },
      refetchInterval: 5000,
    }),
    "balanceQueryParams",
  )

  const logIn = atom<{
    logIn: () => void
  }>(
    {
      logIn: () => {},
    },
    "logIn",
  )

  const logOut = atom<{
    logOut: () => void
  }>(
    {
      logOut: () => {},
    },
    "logOut",
  )

  const linkAdditionalWallet = atom<{
    linkEvm: () => void
    linkSolana: () => void
    /** Opens the wallet prompt for a Profile EVM wallet that cannot sign. */
    reconnectEvm: () => EvmReconnectResult
  }>(
    {
      linkEvm: () => {},
      linkSolana: () => {},
      reconnectEvm: (): EvmReconnectResult => "unavailable",
    },
    "linkAdditionalWallet",
  )

  const getBalanceAtom = atom<{
    getBalance: () => Promise<string | undefined>
  }>(
    {
      getBalance: async () => "0",
    },
    "getBalance",
  )

  const getPublicClient = atom<{
    getPublicClient?: () => Promise<PublicClient<Transport, Chain>>
  }>({ getPublicClient: undefined }, "getPublicClient")

  const getWalletClient = atom<{
    getWalletClient?: (
      chainId?: number,
    ) => Promise<WalletClient<Transport, Chain, Account>>
  }>({ getWalletClient: undefined }, "getWalletClient")

  const connectWalletButton = atom<{
    ConnectWalletButton: React.ComponentType<{}>
  }>(
    {
      ConnectWalletButton: () => null,
    },
    "connectWalletButton",
  )

  return {
    status,
    evmAddress,
    solanaAddress,
    address,
    chain,
    walletBlockchainNetworkId,
    initialized,
    loginMethod,
    hasEvmWallet,
    hasSolanaWallet,
    evmUnionAddress,
    solanaUnionAddress,
    primaryUnionAddress,
    isConnected,
    isConnecting,
    unionAddress,
    connectedBlockchain,
    balanceQueryParams,
    logIn,
    logOut,
    linkAdditionalWallet,
    getBalanceAtom,
    getPublicClient,
    getWalletClient,
    connectWalletButton,
  }
})

function getBlockchain(
  allBlockchains: readonly BlockchainConfig[],
  chainGroup: Blockchain.ETHEREUM | Blockchain.SOLANA | undefined,
  connectedNetwork: string | number | undefined,
): Blockchain | undefined {
  if (!connectedNetwork) return undefined

  if (chainGroup === Blockchain.SOLANA) {
    return Blockchain.SOLANA
  }

  return allBlockchains.find(
    (config) => "chainId" in config && config.chainId === connectedNetwork,
  )?.blockchain
}
